import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Auth } from '@angular/fire/auth';
import { AuthService } from '../services/auth.service';
import { UserRolesService } from '../services/user-roles.service';
import { environment } from '@beyou/environments/environment';
import { UserPreferenceService } from '../services/user-preference.service';
import { detectUiLanguageFromBrowser } from '../services/ui-language.service';

/**
 * Attaches the Firebase ID token to API requests. The token is read from the SDK cache, which
 * refreshes it on its own shortly before it expires, so a page load costs no Firebase calls.
 *
 * Custom claims (roles) only reach the client with a new token, so a request denied with 401/403
 * is retried once after a forced refresh: a user whose roles changed server-side picks them up
 * on their next denied call instead of an hour later. One forced refresh per token bounds the
 * cost — a denial that survives it is a real denial.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private forcedToken: string | null = null;

  constructor(
    private firebaseAuth: Auth,
    private authService: AuthService,
    private userRolesService: UserRolesService,
    private userPreferenceService: UserPreferenceService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.shouldAttachFirebaseAuth(req.url)) {
      return next.handle(req);
    }

    return from(this.resolveAuthToken()).pipe(
      switchMap((authToken) => next.handle(this.withAuth(req, authToken))),
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse) || (error.status !== 401 && error.status !== 403)) {
          return throwError(() => error);
        }
        return from(this.forceRefreshToken()).pipe(
          switchMap((freshToken) =>
            freshToken ? next.handle(this.withAuth(req, freshToken)) : throwError(() => error)
          )
        );
      })
    );
  }

  private withAuth(req: HttpRequest<any>, authToken: string): HttpRequest<any> {
    const headers: Record<string, string> = {};

    if (!req.headers.has('Accept-Language')) {
      headers['Accept-Language'] = authToken
        ? this.userPreferenceService.getPreferredLanguageCodeSnapshot()
        : this.resolveUnauthenticatedAcceptLanguage();
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    return req.clone({
      setHeaders: headers,
      params: req.params.set('ts', new Date().getTime().toString()),
    });
  }

  private resolveUnauthenticatedAcceptLanguage(): string {
    if (this.userPreferenceService.hasStoredPreferences()) {
      return this.userPreferenceService.getUiLanguageCodeSnapshot();
    }
    return detectUiLanguageFromBrowser();
  }

  private shouldAttachFirebaseAuth(url: string): boolean {
    if (url.startsWith(environment.adminApiBaseUrl)) {
      return true;
    }
    const subriseApi = environment.subriseApiBaseUrl?.trim() ?? '';
    return subriseApi.length > 0 && url.startsWith(subriseApi);
  }

  private async resolveAuthToken(): Promise<string> {
    const currentUser = this.firebaseAuth.currentUser ?? this.authService._user;
    if (!currentUser) {
      return this.authService.token$.getValue();
    }

    const liveToken = await currentUser.getIdToken();
    this.authService.token$.next(liveToken);
    return liveToken;
  }

  /** `null` when the current token already came from a forced refresh, so a denial is not retried again. */
  private async forceRefreshToken(): Promise<string | null> {
    const currentUser = this.firebaseAuth.currentUser ?? this.authService._user;
    if (!currentUser || (await currentUser.getIdToken()) === this.forcedToken) {
      return null;
    }

    const freshToken = await currentUser.getIdToken(true);
    this.forcedToken = freshToken;
    this.authService.token$.next(freshToken);
    await firstValueFrom(this.userRolesService.resolveCurrentRoles());
    return freshToken;
  }
}
